import React from "react";

export function Badge({ children, variant = "default", style }) {
  const colors = {
    default: { bg:"rgba(15,61,62,0.1)",    color:"#0F3D3E",  border:"1px solid rgba(15,61,62,0.2)" },
    success: { bg:"rgba(34,197,94,0.1)",   color:"#16a34a",  border:"1px solid rgba(34,197,94,0.25)" },
    danger:  { bg:"rgba(239,68,68,0.1)",   color:"#dc2626",  border:"1px solid rgba(239,68,68,0.25)" },
    warning: { bg:"rgba(245,158,11,0.1)",  color:"#d97706",  border:"1px solid rgba(245,158,11,0.25)" },
    info:    { bg:"rgba(59,130,246,0.1)",  color:"#2563eb",  border:"1px solid rgba(59,130,246,0.25)" },
    gold:    { bg:"rgba(208,179,106,0.15)",color:"#a88a45",  border:"1px solid rgba(208,179,106,0.3)" },
  };
  const s = colors[variant] || colors.default;
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", padding:"2px 9px",
      borderRadius:20, fontSize:11, fontWeight:600, letterSpacing:"0.3px",
      textTransform:"uppercase", whiteSpace:"nowrap",
      background:s.bg, color:s.color, border:s.border,
      ...style
    }}>{children}</span>
  );
}

export function Button({ children, variant="primary", size="md", onClick, disabled, type="button", style }) {
  const base = {
    display:"inline-flex", alignItems:"center", gap:6,
    cursor:disabled?"not-allowed":"pointer",
    border:"none", fontFamily:"var(--font-body)", fontWeight:600,
    borderRadius:"var(--radius)", transition:"all 0.15s ease",
    opacity:disabled?0.5:1, whiteSpace:"nowrap",
  };
  const sizes = {
    sm:{ padding:"5px 12px", fontSize:12 },
    md:{ padding:"8px 18px", fontSize:13 },
    lg:{ padding:"11px 24px", fontSize:14 },
  };
  const variants = {
    primary: { background:"#0F3D3E", color:"#ffffff", boxShadow:"0 2px 8px rgba(15,61,62,0.3)" },
    gold:    { background:"linear-gradient(135deg,#D0B36A,#a88a45)", color:"#fff", boxShadow:"0 2px 8px rgba(208,179,106,0.35)" },
    danger:  { background:"rgba(239,68,68,0.08)", color:"#dc2626", border:"1px solid rgba(239,68,68,0.25)" },
    ghost:   { background:"transparent", color:"var(--text-secondary)", border:"1px solid var(--border)" },
    outline: { background:"transparent", color:"#0F3D3E", border:"1px solid #0F3D3E" },
  };
  const s = variants[variant] || variants.primary;
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...sizes[size], ...s, ...style }}
      onMouseEnter={e => !disabled && (e.currentTarget.style.filter="brightness(1.1)")}
      onMouseLeave={e => e.currentTarget.style.filter="none"}>
      {children}
    </button>
  );
}

export function Card({ children, style, onClick }) {
  return (
    <div onClick={onClick} style={{
      background:"var(--bg-card)", border:"1px solid var(--border)",
      borderRadius:"var(--radius-lg)", padding:"20px",
      boxShadow:"var(--shadow-sm)", cursor:onClick?"pointer":undefined,
      transition:"border-color 0.2s, box-shadow 0.2s", ...style,
    }}
    onMouseEnter={e => onClick&&(e.currentTarget.style.boxShadow="var(--shadow)")}
    onMouseLeave={e => onClick&&(e.currentTarget.style.boxShadow="var(--shadow-sm)")}>
      {children}
    </div>
  );
}

export function Input({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  required,
  disabled = false,
  style,
  icon
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {label && (
        <label style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 }}>
          {label}
        </label>
      )}

      <div style={{ position: "relative" }}>
        {icon && (
          <span
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-muted)",
              opacity: disabled ? 0.5 : 1,
            }}
          >
            {icon}
          </span>
        )}

        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          style={{
            width: "100%",
            padding: icon ? "9px 12px 9px 34px" : "9px 12px",
            background: disabled ? "#f3f4f6" : "var(--bg-input)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
            color: disabled ? "var(--text-muted)" : "var(--text-primary)",
            fontSize: 13,
            outline: "none",
            fontFamily: "var(--font-body)",
            cursor: disabled ? "not-allowed" : "text",
            opacity: disabled ? 0.7 : 1,
            ...style,
          }}
          onFocus={e => {
            if (!disabled) e.target.style.borderColor = "#0F3D3E";
          }}
          onBlur={e => {
            e.target.style.borderColor = "var(--border)";
          }}
        />
      </div>
    </div>
  );
}

export function Select({ label, value, onChange, options, style }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
      {label&&<label style={{ fontSize:12, color:"var(--text-secondary)", fontWeight:500 }}>{label}</label>}
      <select value={value} onChange={onChange}
        style={{
          padding:"9px 12px", background:"var(--bg-input)", border:"1px solid var(--border)",
          borderRadius:"var(--radius)", color:"var(--text-primary)", fontSize:13,
          outline:"none", fontFamily:"var(--font-body)", cursor:"pointer", ...style,
        }}>
        {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function Table({ columns, data, emptyText="No data" }) {
  return (
    <div style={{ overflowX:"auto" }}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr style={{ borderBottom:"2px solid var(--border)", background:"rgba(15,61,62,0.03)" }}>
            {columns.map(col=>(
              <th key={col.key} style={{
                padding:"10px 14px", textAlign:"left", fontSize:11,
                color:"var(--text-muted)", fontWeight:600,
                textTransform:"uppercase", letterSpacing:"0.6px", whiteSpace:"nowrap",
              }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length===0?(
            <tr><td colSpan={columns.length} style={{ padding:"32px", textAlign:"center", color:"var(--text-muted)" }}>{emptyText}</td></tr>
          ):data.map((row,i)=>(
            <tr key={i} style={{ borderBottom:"1px solid var(--border)" }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(15,61,62,0.025)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              {columns.map(col=>(
                <td key={col.key} style={{ padding:"11px 14px", fontSize:13, color:"var(--text-primary)", verticalAlign:"middle" }}>
                  {col.render?col.render(row[col.key],row):(row[col.key]??'—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({ page, totalPages, onChange }) {
  if (totalPages<=1) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:8, padding:"14px 0 0" }}>
      <Button variant="ghost" size="sm" onClick={()=>onChange(page-1)} disabled={page===0}>← Prev</Button>
      <span style={{ fontSize:12, color:"var(--text-secondary)" }}>Page {page+1} of {totalPages}</span>
      <Button variant="ghost" size="sm" onClick={()=>onChange(page+1)} disabled={page>=totalPages-1}>Next →</Button>
    </div>
  );
}

// ✅ CORRECT — Square inside a square at bottom-right corner
export function StatCard({ title, value, icon, sub, gradient, loading }) {
  return (
    <div style={{
      background: "var(--bg-card)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-lg)",
      padding: "18px 20px",
      position: "relative",
      overflow: "hidden",           // ✅ clips the squares at card corners
      minHeight: 115,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      boxShadow: "var(--shadow-sm)",
    }}>

      {gradient && (
        <>
          {/* ✅ BIG square — flush to bottom-right corner, 60% of card height */}
          <div style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 75,
            height: 75,
            borderRadius: "14px 0 0 0",   // only top-left corner rounded, other 3 sides flush to card
            background: gradient,
            opacity: 0.6,
            pointerEvents: "none",
          }} />

          {/* ✅ SMALL square — sits inside big square, shares same bottom-right corner */}
          <div style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 55,
            height: 55,
            borderRadius: "10px 0 0 0",   // only top-left corner rounded
            background: gradient,
            opacity: 1,
            pointerEvents: "none",
          }} />
        </>
      )}

      {/* Content */}
      <div style={{ position: "relative", zIndex: 1 }}>
        {icon && (
          <div style={{ fontSize: 20, marginBottom: 8, lineHeight: 1 }}>
            {icon}
          </div>
        )}

        <div style={{
          fontSize: 11, fontWeight: 600,
          textTransform: "uppercase", letterSpacing: "0.5px",
          color: "var(--text-muted)", marginBottom: 10,
        }}>
          {title}
        </div>

        {loading ? (
          <div style={{
            height: 28, width: "55%", borderRadius: 6,
            background: "var(--border)", opacity: 0.5,
          }} />
        ) : (
          <div style={{
            fontSize: 26, fontWeight: 700,
            fontFamily: "var(--font-display)",
            color: "var(--text-primary)",
            lineHeight: 1,
          }}>
            {value ?? "—"}
          </div>
        )}

        {sub && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 5 }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, width=480 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, background:"rgba(15,30,30,0.45)",
      backdropFilter:"blur(4px)", zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center",
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"#fff", border:"1px solid var(--border-strong)",
        borderRadius:"var(--radius-xl)", padding:28, width, maxWidth:"95vw",
        boxShadow:"var(--shadow-lg)", maxHeight:"90vh", overflowY:"auto",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h3 style={{ fontFamily:"var(--font-display)", fontSize:16, color:"#0F3D3E" }}>{title}</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--text-muted)", cursor:"pointer", fontSize:18 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
      <div>
        <h1 style={{ fontFamily:"var(--font-display)", fontSize:22, color:"var(--text-primary)", marginBottom:4 }}>{title}</h1>
        {subtitle&&<p style={{ fontSize:13, color:"var(--text-muted)" }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Toggle({ value, onChange }) {
  return (
    <div onClick={()=>onChange(!value)} style={{
      width:40, height:22, borderRadius:11, cursor:"pointer",
      background:value?"#0F3D3E":"#e2e8f0",
      position:"relative", transition:"background 0.2s",
      flexShrink:0,
    }}>
      <div style={{
        position:"absolute", top:3, left:value?19:3, width:16, height:16,
        borderRadius:"50%", background:"#fff",
        transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)",
      }}/>
    </div>
  );
}
